import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LiveMap } from "@/components/LiveMap";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Radio, Users, UserCheck, Eye, Globe, ArrowLeft, MapPin, LayoutDashboard, Circle,
  Activity, ScanLine, Wrench, MessageCircle, Home, Route, Wallet, ParkingSquare,
  ShieldAlert, ShieldCheck, Heart, User, Crown, MoreHorizontal, Car,
} from "lucide-react";

const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const PATH_FEATURE = {
  "/": "Landing", "/login": "Login", "/register": "Sign Up", "/onboarding": "Onboarding",
  "/app": "Dashboard", "/app/scan": "AI Scan", "/app/assistant": "Ask VecPadi",
  "/app/health": "Vehicle Health", "/app/drive": "Drive", "/app/care": "Car Care",
  "/app/expenses": "Expenses", "/app/parking": "Parking", "/app/safety": "Safety",
  "/app/insurance": "Insurance", "/app/profile": "Profile", "/app/premium": "Premium",
  "/app/more": "More", "/admin": "Admin", "/admin/live": "Admin Live",
};
const pathFeature = (p) => PATH_FEATURE[(p || "/").replace(/\/$/, "") || "/"] || "Other";

const FEATURE_META = {
  "AI Scan": { icon: ScanLine, color: "text-emerald-600 bg-emerald-50" },
  "Car Care": { icon: Wrench, color: "text-amber-600 bg-amber-50" },
  "Ask VecPadi": { icon: MessageCircle, color: "text-blue-600 bg-blue-50" },
  "Dashboard": { icon: Home, color: "text-indigo-600 bg-indigo-50" },
  "Drive": { icon: Route, color: "text-teal-600 bg-teal-50" },
  "Expenses": { icon: Wallet, color: "text-amber-600 bg-amber-50" },
  "Parking": { icon: ParkingSquare, color: "text-indigo-600 bg-indigo-50" },
  "Safety": { icon: ShieldAlert, color: "text-red-600 bg-red-50" },
  "Insurance": { icon: ShieldCheck, color: "text-teal-600 bg-teal-50" },
  "Vehicle Health": { icon: Heart, color: "text-emerald-600 bg-emerald-50" },
  "Profile": { icon: User, color: "text-zinc-600 bg-zinc-100" },
  "Premium": { icon: Crown, color: "text-amber-600 bg-amber-50" },
  "More": { icon: MoreHorizontal, color: "text-zinc-600 bg-zinc-100" },
  "Landing": { icon: Globe, color: "text-zinc-600 bg-zinc-100" },
  "Login": { icon: User, color: "text-zinc-600 bg-zinc-100" },
  "Sign Up": { icon: User, color: "text-zinc-600 bg-zinc-100" },
  "Onboarding": { icon: Car, color: "text-emerald-600 bg-emerald-50" },
  "Admin": { icon: LayoutDashboard, color: "text-zinc-600 bg-zinc-100" },
  "Admin Live": { icon: Radio, color: "text-emerald-600 bg-emerald-50" },
};
const featureMeta = (f) => FEATURE_META[f] || { icon: Activity, color: "text-zinc-600 bg-zinc-100" };

export default function SuperAdmin() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const load = () => api.get("/admin/live").then((r) => setData(r.data)).catch(() => {});
    load();
    timer.current = setInterval(() => { load(); setTick((t) => t + 1); }, 5000);
    return () => clearInterval(timer.current);
  }, []);

  const stats = data ? [
    { icon: Radio, label: "Online Now", value: data.online_now, tone: "text-emerald-600 bg-emerald-50", pulse: true },
    { icon: UserCheck, label: "Users Online", value: data.online_users, tone: "text-blue-600 bg-blue-50" },
    { icon: Eye, label: "Guests Online", value: data.online_guests, tone: "text-amber-600 bg-amber-50" },
    { icon: Users, label: "Today", value: data.today_total, tone: "text-indigo-600 bg-indigo-50" },
    { icon: Globe, label: "All Time", value: data.all_time, tone: "text-zinc-600 bg-zinc-100" },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo showText={false} />
            <div className="flex items-center gap-2">
              <Radio size={18} className="text-emerald-500" />
              <span className="font-display font-bold">Super Admin — Live</span>
              <span className="ml-1 flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                LIVE
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => navigate("/admin")}><LayoutDashboard size={14} /> Admin</Button>
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => navigate("/app")}><ArrowLeft size={14} /> App</Button>
            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200" onClick={async () => { await logout(); navigate("/"); }}>Log out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-testid="superadmin-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold tracking-tight">Real-time visitors — Nigeria</h1>
          <span className="text-xs text-zinc-400">Auto-refreshing every 5s</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-5">
          {!data ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-3xl" />) :
            stats.map((c) => (
              <div key={c.label} className="rounded-3xl border border-zinc-200 bg-white p-5" data-testid="live-stat">
                <div className={`grid h-10 w-10 place-items-center rounded-xl ${c.tone}`}>
                  <c.icon size={20} className={c.pulse ? "animate-pulse" : ""} />
                </div>
                <div className="mt-3 font-display text-2xl font-bold">{c.value}</div>
                <div className="text-xs text-zinc-400">{c.label}</div>
              </div>
            ))}
        </div>

        {/* Live Feature Pulse */}
        <div className="mt-6 rounded-3xl border border-zinc-200 bg-white p-5" data-testid="feature-pulse">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold flex items-center gap-2"><Activity size={16} className="text-emerald-600" /> Live Feature Pulse</h3>
            <span className="text-xs text-zinc-400">What people dey use right now</span>
          </div>
          {data && Object.keys(data.by_feature || {}).length > 0 ? (
            <div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {Object.entries(data.by_feature).map(([f, n], i) => {
                const m = featureMeta(f);
                const pct = data.online_now ? Math.round((n / data.online_now) * 100) : 0;
                return (
                  <div key={f} className="flex items-center gap-3" data-testid="feature-row">
                    <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${m.color}`}><m.icon size={18} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-800">
                          {f}
                          {i === 0 && n > 0 && <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-600">🔥 TOP</span>}
                        </span>
                        <span className="text-sm font-semibold text-zinc-700">{n}</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-zinc-100">
                        <div className="h-2 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="mt-3 text-sm text-zinc-400">No one online right now — waiting for visitors…</p>}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Map */}
          <div className="lg:col-span-2">
            <div className="mb-3 flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><Circle size={10} className="fill-emerald-500 text-emerald-500" /> Registered users</span>
              <span className="flex items-center gap-1.5"><Circle size={10} className="fill-amber-500 text-amber-500" /> Guests</span>
            </div>
            {!data ? <Skeleton className="h-[440px] rounded-3xl" /> : <LiveMap points={data.online} />}
          </div>

          {/* By state + online list */}
          <div className="space-y-4">
            <div className="rounded-3xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display font-semibold flex items-center gap-2"><MapPin size={16} className="text-emerald-600" /> By state</h3>
              <div className="mt-3 space-y-2">
                {data && Object.keys(data.by_state).length > 0 ? Object.entries(data.by_state).map(([s, n]) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">{s}</span>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">{n} online</span>
                  </div>
                )) : <p className="text-sm text-zinc-400">No one online right now.</p>}
              </div>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-5">
              <h3 className="font-display font-semibold">Live sessions</h3>
              <div className="mt-3 max-h-64 space-y-2 overflow-y-auto no-scrollbar">
                {data && data.online.length > 0 ? data.online.map((v) => (
                  <div key={v.session_id} className="flex items-center gap-3 rounded-2xl border border-zinc-100 p-3" data-testid="live-session">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${v.is_user ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-zinc-800">{v.is_user ? (v.user_name || "User") : "Guest visitor"}</div>
                      <div className="truncate text-xs text-zinc-400">{v.city}, {v.state} · <span className="font-medium text-zinc-500">{pathFeature(v.path)}</span></div>
                    </div>
                    <span className="shrink-0 text-[11px] text-zinc-400">{ago(v.last_seen)}</span>
                  </div>
                )) : <p className="text-sm text-zinc-400">Waiting for visitors…</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Recent feed */}
        <div className="mt-6 rounded-3xl border border-zinc-200 bg-white p-5">
          <h3 className="font-display font-semibold">Recent activity</h3>
          <div className="mt-3 divide-y divide-zinc-100">
            {data && data.recent.length > 0 ? data.recent.map((v, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${v.is_user ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <span className="text-zinc-700">{v.is_user ? (v.user_name || "User") : "Visitor"}</span>
                  <span className="text-zinc-400">· {v.city}, {v.state}{v.country && v.country !== "Nigeria" ? ` (${v.country})` : ""}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline text-xs font-medium text-zinc-500">{pathFeature(v.path)}</span>
                  <span className="text-xs text-zinc-400">{ago(v.last_seen)}</span>
                </div>
              </div>
            )) : <p className="py-2 text-sm text-zinc-400">No activity yet.</p>}
          </div>
        </div>
      </main>
    </div>
  );
}
